import { useState, useEffect } from "react";
import {
	Dialog,
	DialogContent,
	DialogHeader,
	DialogTitle,
	DialogFooter,
} from "../../../../components/ui/dialog";
import { Button } from "../../../../components/ui/button";
import { Label } from "../../../../components/ui/label";
import { Input } from "../../../../components/ui/input";
import { RadioGroup, RadioGroupItem } from "../../../../components/ui/radio-group";
import { PatientCommandDialog } from "@/dentlab/src/pages/job-page/job-page-components/patient-info-dialog/components/patient-command-dialog.component";
import { GuarantorCommandDialog } from "./guarantor-command-dialog.component";
import { ClientCommandDialog } from "./client-command-dialog.component";
import { useCentralStore } from "@/dentlab/src/store/Central";
import { usePatientStore } from "@/dentlab/src/store/Patient";
import { useAccountingStore } from "@/dentlab/src/store/Accounting";
import { getGuarantorDisplayName } from "@/dentlab/src/lib/utils/guarantor-utils";
import { getClientDisplayName } from "@/lib/utils/client-utils";
import { AlertTriangle, Loader2 } from "lucide-react";
import { useExportActions } from "@/dentlab/src/hooks/actions/useExportActions";
import {
	ClientEntityType,
	GuarantorEntityType,
	PatientEntityType,
	JobEntityType,
	JobDocumentEntityType,
} from "@/lib/supabase/supabaseTypes";
import { LazyPatient } from "@/dentlab/src/store/Patient/patients.store";
import { getBankAccountForAkonto, validateAkontoData } from "../utils/akonto-validation";
import { PaymentSlipDataInput } from "@/dentlab/src/pdf-templates/pdf-template-components/qr-payment-slip/qr-payment-helpers";
import { Currency } from "@/dentlab/src/types/enums";

type RecipientType = "patient" | "guarantor" | "client";

interface CreateAkontoInvoiceDialogProps {
	open: boolean;
	onOpenChange: (open: boolean) => void;
	onSuccess?: () => void;
}

export const CreateAkontoInvoiceDialog: React.FC<CreateAkontoInvoiceDialogProps> = ({
	open,
	onOpenChange,
	onSuccess,
}) => {
	const [recipientType, setRecipientType] = useState<RecipientType>("client");
	const [selectedRecipientId, setSelectedRecipientId] = useState<string | null>(null);
	const [amount, setAmount] = useState<string>("");
	const [patientDialogOpen, setPatientDialogOpen] = useState(false);
	const [guarantorDialogOpen, setGuarantorDialogOpen] = useState(false);
	const [clientDialogOpen, setClientDialogOpen] = useState(false);
	const [isSubmitting, setIsSubmitting] = useState(false);
	const [error, setError] = useState<string | null>(null);
	const [hasStartedCreation, setHasStartedCreation] = useState(false);

	const { guarantorLookup, clientsLookup } = useCentralStore((state) => ({
		guarantorLookup: state.guarantorLookup,
		clientsLookup: state.clientsLookup,
	}));

	const { patientName, patientsLookup } = usePatientStore((state) => ({
		patientName: state.patientName,
		patientsLookup: state.patientsLookup,
	}));

	const { getAkonto, akontoLookup, createAkontoInvoice } = useAccountingStore((state) => ({
		getAkonto: state.getAkonto,
		akontoLookup: state.akontoLookup,
		createAkontoInvoice: state.createAkontoInvoice,
	}));

	const { exportAkontorechnung } = useExportActions();


	const selectedRecipient =
		recipientType === "patient"
			? patientsLookup[selectedRecipientId || ""]
			: recipientType === "guarantor"
			? guarantorLookup[selectedRecipientId || ""]
			: clientsLookup[selectedRecipientId || ""];

	const selectedRecipientName =
		recipientType === "patient" && selectedRecipient
			? patientName(selectedRecipient as any)
			: recipientType === "guarantor" && selectedRecipient
			? getGuarantorDisplayName(selectedRecipient as any)
			: recipientType === "client" && selectedRecipient
			? getClientDisplayName(selectedRecipient as any)
			: "";

	const getAkontoKey = (type: RecipientType, id: string) => {
		return `${type === "client" ? "client" : type === "guarantor" ? "guarantor" : "patient"}:${id}`;
	};

	const existingAkonto = selectedRecipientId
		? akontoLookup[getAkontoKey(recipientType, selectedRecipientId)] || 0
		: 0;

	const handleReset = () => {
		setRecipientType("client");
		setSelectedRecipientId(null);
		setAmount("");
		setError(null);
		setHasStartedCreation(false);
	};

	const handleOpenChange = (newOpen: boolean) => {
		if (!newOpen) {
			handleReset();
		}
		onOpenChange(newOpen);
	};

	const handleRecipientSelect = (Id: string | null,type: RecipientType) => {
		if(!Id) {
			return;
		}
		setSelectedRecipientId(Id);
		switch (type) {
			case "patient":
				setPatientDialogOpen(false);
				getAkonto(null, null, Id); 
				break;
			case "client":
				setClientDialogOpen(false);
				getAkonto(Id, null, null);
				break;
			case "guarantor":
				setGuarantorDialogOpen(false);
				getAkonto(null, Id, null);
				break;
		}
	};

	const handleSubmit = async () => {
		setError(null);

		if (!selectedRecipientId) {
			setError("Bitte wählen Sie einen Empfänger aus");
			return;
		}

		const amountNum = parseFloat(amount);
		if (isNaN(amountNum) || amountNum <= 0) {
			setError("Bitte geben Sie einen gültigen Betrag ein");
			return;
		}

		setIsSubmitting(true);
		setHasStartedCreation(true);

		try {
			const { organization, bankAccountsLookup } = useCentralStore.getState();

			if (!organization) {
				setError("Organisation nicht gefunden");
				setIsSubmitting(false);
				return;
			}

			let recipientForBankAccount: ClientEntityType | GuarantorEntityType;
			let recipientForValidation: ClientEntityType | GuarantorEntityType | PatientEntityType;
			let clientForFile: ClientEntityType | undefined;
			let guarantor: GuarantorEntityType | undefined;
			let patient: PatientEntityType | undefined;

			if (recipientType === "client") {
				const client = clientsLookup[selectedRecipientId];
				if (!client) {
					setError("Kundendaten nicht gefunden");
					setIsSubmitting(false);
					return;
				}
				recipientForBankAccount = client;
				recipientForValidation = client;
				clientForFile = client;
			} else if (recipientType === "guarantor") {
				guarantor = guarantorLookup[selectedRecipientId];
				if (!guarantor) {
					setError("Garantendaten nicht gefunden");
					setIsSubmitting(false);
					return;
				}
				const clientWithGuarantor = Object.values(clientsLookup).find(c => c.guarantor_id === selectedRecipientId);
				if (!clientWithGuarantor) {
					setError("Kunde für Garant nicht gefunden");
					setIsSubmitting(false);
					return;
				}
				recipientForBankAccount = guarantor;
				recipientForValidation = guarantor;
				clientForFile = clientWithGuarantor;
			} else {
				patient = await usePatientStore.getState().getPatient(selectedRecipientId) as PatientEntityType;
				if (!patient) {
					setError("Patientendaten konnten nicht geladen werden");
					setIsSubmitting(false);
					return;
				}
				const client = clientsLookup[patient.client_id || ""];
				if (!client) {
					setError("Kunde für Patient nicht gefunden");
					setIsSubmitting(false);
					return;
				}
				recipientForBankAccount = client;
				recipientForValidation = patient;
				clientForFile = client;
			}

			const { bankAccount, error: bankAccountError } = getBankAccountForAkonto({
				bankAccountsLookup,
				recipient: recipientForBankAccount,
				organization,
			});

			if (!bankAccount || bankAccountError) {
				setError(bankAccountError || "Kein gültiges Bankkonto gefunden");
				setIsSubmitting(false);
				return;
			}

			const validationResult = validateAkontoData({
				bankAccount,
				recipient: recipientForValidation,
			});

			if (!validationResult.valid) {
				setError(validationResult.error || "Validierung fehlgeschlagen");
				setIsSubmitting(false);
				return;
			}

			const { invoiceNumber, filePath } = await createAkontoInvoice({
				recipientType,
				recipientId: selectedRecipientId,
				amount: amountNum,
				bankAccountId: bankAccount.id,
			});

			const paymentSlipData: PaymentSlipDataInput = {
				bankAccount,
				recipient: recipientForValidation,
				amount: amountNum.toFixed(2),
				invoiceNumber,
				currency: Currency.CHF,
			};

			const zeroValue = { value: 0, text: "0.00" };
			const documentProps = {
				organization,
				client: clientForFile,
				job: {
					mwst_number: clientForFile.mwst_number || organization.mwst_number,
				} as JobEntityType,
				jobDocument: {} as JobDocumentEntityType,
				jobItems: [],
				jobTotals: {
					taxpunkte: zeroValue,
					amountPostage: zeroValue,
					amountExternalWork: zeroValue,
					amountDiscounts: zeroValue,
					amountWorkAndFixedRate: zeroValue,
					deductions: zeroValue,
					deductionsWorkAndFixedRate: zeroValue,
					deductionsMaterials: zeroValue,
					amountMaterials: zeroValue,
					subTotal: zeroValue,
					mwst: zeroValue,
					total: zeroValue,
					exemptFromTaxAmountExclVat: zeroValue,
					exemptFromTaxAmountInclVat: zeroValue,
					subjectToDrTaxAmountExclVat: zeroValue,
					subjectToDrTaxAmountInclVat: zeroValue,
					mwstPercentage: "",
				},
				guarantor: recipientType === "guarantor" ? guarantor : undefined,
				patient: recipientType === "patient" ? patient : undefined,
				practitioner: undefined,
			};

			const { success: pdfSuccess, error: pdfError } = await exportAkontorechnung(
				documentProps,
				paymentSlipData,
				new Date().toISOString(),
				filePath,
				false
			);

			if (!pdfSuccess) {
				setError("Rechnung erstellt, aber PDF-Generierung fehlgeschlagen: " + (pdfError || "Unbekannter Fehler"));
				setIsSubmitting(false);
				return;
			}

			handleReset();
			onOpenChange(false);
			onSuccess?.();
		} catch (err) {
			setError("Ein unerwarteter Fehler ist aufgetreten: " + (err instanceof Error ? err.message : String(err)));
		} finally {
			setIsSubmitting(false);
		}
	};

	const canSubmit = selectedRecipientId && amount && parseFloat(amount) > 0 && !isSubmitting;

	return (
		<>
			<Dialog open={open} onOpenChange={handleOpenChange}>
				<DialogContent className="max-w-2xl" aria-label="Akontorechnung erstellen">
					<DialogHeader>
						<DialogTitle>Akontorechnung erstellen</DialogTitle>
					</DialogHeader>

					<div className="space-y-6">
						{/* Step 1: Recipient Type Selection */}
						<div className="space-y-3">
							<Label>Empfängertyp</Label>
							<RadioGroup
								value={recipientType}
								onValueChange={(value) => {
									setRecipientType(value as RecipientType);
									setSelectedRecipientId(null);
								}}
								className="flex gap-4"
							>
								<div className="flex items-center space-x-2">
									<RadioGroupItem
										value="client"
										id="client"
										data-testid="recipient-type-client"
									/>
									<Label htmlFor="client" className="font-normal cursor-pointer">
										Kunde
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem
										value="guarantor"
										id="guarantor"
										data-testid="recipient-type-guarantor"
									/>
									<Label htmlFor="guarantor" className="font-normal cursor-pointer">
										Garant
									</Label>
								</div>
								<div className="flex items-center space-x-2">
									<RadioGroupItem
										value="patient"
										id="patient"
										data-testid="recipient-type-patient"
									/>
									<Label htmlFor="patient" className="font-normal cursor-pointer">
										Patient
									</Label>
								</div>
							</RadioGroup>
						</div>

						{/* Step 2: Recipient Selection */}
						<div className="space-y-3">
							<Label>Empfänger auswählen</Label>
							{recipientType === "patient" ? (
								<Button
									type="button"
									variant="outline"
									onClick={() => setPatientDialogOpen(true)}
									className="w-full justify-start"
									data-testid="select-patient-button"
									aria-label="Patient auswählen"
								>
									{selectedRecipientName || "Patient suchen..."}
								</Button>
							) : recipientType === "guarantor" ? (
								<Button
									type="button"
									variant="outline"
									onClick={() => setGuarantorDialogOpen(true)}
									className="w-full justify-start"
									data-testid="select-guarantor-button"
									aria-label="Garant auswählen"
								>
									{selectedRecipientName || "Garant suchen..."}
								</Button>
							) : (
								<Button
									type="button"
									variant="outline"
									onClick={() => setClientDialogOpen(true)}
									className="w-full justify-start"
									data-testid="select-client-button"
									aria-label="Kunde auswählen"
								>
									{selectedRecipientName || "Kunde suchen..."}
								</Button>
							)}
						</div>

						{/* Warning for existing akonto */}
						{selectedRecipientId && existingAkonto > 0 && !hasStartedCreation && (
							<div className="p-3 bg-orange-50 border border-orange-200 rounded-md flex items-start gap-2">
								<AlertTriangle className="h-5 w-5 text-orange-600 flex-shrink-0 mt-0.5" />
								<div className="text-sm text-orange-700">
									<p className="font-medium text-orange-800 mb-1">Bestehendes Akonto-Guthaben</p>
									<p>Aktuelles Guthaben: <span className="font-mono">CHF {existingAkonto.toFixed(2)}</span></p>
									<p className="text-xs text-orange-600 mt-1">Das neue Akonto wird zum bestehenden Guthaben hinzugefügt.</p>
								</div>
							</div>
						)}

						{/* Step 3: Amount Input */}
						{selectedRecipientId && (
							<div className="space-y-3">
								<Label htmlFor="amount">Betrag (CHF)</Label>
								<Input
									id="amount"
									type="number"
									step="0.01"
									min="0"
									value={amount}
									onChange={(e) => setAmount(e.target.value)}
									placeholder="0.00"
									data-testid="amount-input"
									aria-label="Betrag in CHF"
								/>
							</div>
						)}

						{/* Error Display */}
						{error && (
							<div className="p-3 bg-red-50 border border-red-200 rounded-md text-sm text-red-800">
								{error}
							</div>
						)}
					</div>

					<DialogFooter>
						<Button
							type="button"
							variant="outline"
							onClick={() => handleOpenChange(false)}
							disabled={isSubmitting}
						>
							Abbrechen
						</Button>
						<Button
							type="button"
							onClick={handleSubmit}
							disabled={!canSubmit}
							data-testid="create-akonto-button"
							aria-label="Akontorechnung erstellen"
						>
							{isSubmitting ? (
								<>
									<Loader2 className="mr-2 h-4 w-4 animate-spin" />
									Wird erstellt...
								</>
							) : (
								"Erstellen"
							)}
						</Button>
					</DialogFooter>
				</DialogContent>
			</Dialog>

			{/* Patient Search Dialog */}
			<PatientCommandDialog
				open={patientDialogOpen}
				setOpen={setPatientDialogOpen}
				onNewBlankPatient={() => {}}
				onNewPatientWithPrefill={() => {}}
				onPatientSelect={(patientId) => handleRecipientSelect(patientId, "patient")}
			/>

			{/* Guarantor Search Dialog */}
			<GuarantorCommandDialog
				open={guarantorDialogOpen}
				setOpen={setGuarantorDialogOpen}
				onGuarantorSelect={(guarantorId) => handleRecipientSelect(guarantorId, "guarantor")}
			/>

			{/* Client Search Dialog */}
			<ClientCommandDialog
				open={clientDialogOpen}
				setOpen={setClientDialogOpen}
				onClientSelect={(clientId) => handleRecipientSelect(clientId, "client")}
			/>
		</>
	);
};
